import { useCallback, useRef } from "react";

interface UseFilePickerOptions {
  accept?: string;
  multiple?: boolean;
}

interface UseFilePickerResult {
  triggerFilePicker: () => void;
}

/**
 * Hook that provides a way to trigger the browser's native file picker dialog
 * and handle file selection without rendering any DOM elements.
 *
 * @example
 * const MyComponent = () => {
 *   const { triggerFilePicker } = useFilePicker(
 *     (files) => console.log('Files selected:', files),
 *     { accept: '.pdf' }
 *   );
 *
 *   return <button onClick={triggerFilePicker}>Pick File</button>;
 * };
 */
export const useFilePicker = (
  onFileSelect: (files: FileList | null) => void,
  { accept, multiple = false }: UseFilePickerOptions = {}
): UseFilePickerResult => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const triggerFilePicker = useCallback(() => {
    if (!inputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.style.display = "none";
      if (accept) input.accept = accept;
      input.multiple = multiple;

      input.addEventListener("change", (event) => {
        const files = (event.target as HTMLInputElement).files;
        onFileSelect(files);
        // Cleanup
        document.body.removeChild(input);
        inputRef.current = null;
      });

      document.body.appendChild(input);
      inputRef.current = input;
    }

    inputRef.current.click();
  }, [onFileSelect, accept, multiple]);

  return { triggerFilePicker };
};
